import React from "react";
import { useNavigate } from "react-router-dom";
import { useConfig } from "../../../../../../app/configcontext";
import { useDispatch, useSelector } from "react-redux";
import { businessSlice } from "../../../../../../features/business/businessSlice";
import ProtectedPaymentRoute from "../../../../../auth/Auth";

const PLAN_META = {
  OA1: {
    icon: "fa-solid fa-file-lines",
    color: "#1a73e8",
    bg: "#e8f0fe",
    badge: null,
    features: [
      "Official Operating Agreement document",
      "Customizable legal member provisions",
      "Instant PDF download & access",
    ],
  },
  OA2: {
    icon: "fa-solid fa-building",
    color: "#1a73e8",
    bg: "#e8f0fe",
    badge: "Most Popular",
    badgeColor: "#1a73e8",
    features: [
      "Official Operating Agreement document",
      "Federal Tax ID (EIN) Obtainment",
      "IRS tax compliance & banking ready",
      "Open business checking account",
    ],
  },
  OA3: {
    icon: "fa-solid fa-shield-halved",
    color: "#e37400",
    bg: "#fef7e0",
    badge: "Best Value",
    badgeColor: "#e37400",
    features: [
      "Operating Agreement plus EIN",
      "State & local license research",
      "Compliance calendar alerts",
      "License filing assistance",
    ],
  },
  BR1: {
    icon: "fa-solid fa-file-lines",
    color: "#1a73e8",
    bg: "#e8f0fe",
    badge: null,
    features: [
      "Corporate Bylaws document",
      "Initial Board Resolutions",
      "Shareholder meeting minutes template",
      "Customizable corporate provisions",
    ],
  },
  BR2: {
    icon: "fa-solid fa-building",
    color: "#1a73e8",
    bg: "#e8f0fe",
    badge: "Most Popular",
    badgeColor: "#1a73e8",
    features: [
      "Corporate Bylaws & Resolutions",
      "Employer Identification Number (EIN)",
      "Federal Tax ID obtainment from IRS",
      "Bank account & payroll registration",
    ],
  },
  BR3: {
    icon: "fa-solid fa-shield-halved",
    color: "#e37400",
    bg: "#fef7e0",
    badge: "Best Value",
    badgeColor: "#e37400",
    features: [
      "Bylaws, Resolutions & Federal EIN",
      "State & municipal business licenses",
      "Ongoing compliance monitoring",
      "Comprehensive filing support",
    ],
  },
};

const DEFAULT_META = {
  icon: "fa-solid fa-file-contract",
  color: "#1a73e8",
  bg: "#e8f0fe",
  badge: null,
  features: ["Customizable document", "Professional legal template", "Instant access"],
};

export default function Agreements() {
  ProtectedPaymentRoute();

  const config = useConfig();
  const dispatch = useDispatch();
  const actions = businessSlice.actions;
  const navigate = useNavigate();
  const savedBusinessTypeId = useSelector((state) => state.business.businessTypeId);

  const operationAgreement =
    config.operationAgreement &&
    config.operationAgreement.filter((item) => item.businessTypeId === savedBusinessTypeId);

  const visibleAgreements = (operationAgreement || []).filter(
    (ele) => !(ele.businessTypeId === 1 && ele.code === "OA3")
  );

  const handleSelect = (id) => {
    dispatch(actions.update_business_agreement(id));
    navigate(savedBusinessTypeId === 1 ? "/report" : "/total_Complaint");
  };

  const handleSkip = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    dispatch(actions.update_business_agreement(null));
    navigate(savedBusinessTypeId === 1 ? "/report" : "/total_Complaint");
  };

  const backbtn = () => {
    navigate(savedBusinessTypeId === 1 ? "/llc_complian" : "/register_agent_corpo");
  };

  return (
    <div className="min-h-[75vh] bg-[#f8f9fa] py-10 lg:py-16 font-sans">
      <div className="container mx-auto px-4 lg:px-8 max-w-6xl">
        
        {/* Header */}
        <div className="mb-12 text-center max-w-3xl mx-auto">
          <span className="text-[#1a73e8] font-semibold tracking-wider uppercase text-xs mb-2 block">
            Essential Entity Documentation
          </span>
          <h1 className="text-3xl sm:text-4xl font-normal text-[#202124] tracking-tight mb-3">
            Save time and money on essential documents
          </h1>
          <p className="text-[15px] text-[#5f6368] leading-relaxed">
            From official corporate bylaws and operating agreements to Federal Tax IDs (EIN), obtain and customize the legal documentation you need to protect and operate your company.
          </p>
        </div>

        {/* Dynamic 1-Row Responsive Cards Grid */}
        <div
          className={`grid grid-cols-1 ${
            visibleAgreements.length === 3 ? "lg:grid-cols-3 max-w-6xl" : "md:grid-cols-2 max-w-4xl"
          } gap-8 mb-12 mx-auto items-stretch`}
        >
          {visibleAgreements &&
            visibleAgreements.map((ele) => {
              const meta = PLAN_META[ele.code] || DEFAULT_META;
              const isPopular = meta.badge === "Most Popular";

              return (
                <div
                  key={ele.id}
                  className={`bg-white rounded-[12px] p-8 flex flex-col justify-between transition-all duration-300 relative ${
                    isPopular
                      ? "border-2 border-[#1a73e8] shadow-[0_16px_40px_rgba(26,115,232,0.14)] lg:-translate-y-2.5 z-10"
                      : "border border-[#dadce0] shadow-sm hover:border-[#1a73e8] hover:shadow-md"
                  }`}
                >
                  {/* Most Popular / Best Value Badge */}
                  {meta.badge && (
                    <div
                      className={`absolute -top-3.5 left-1/2 -translate-x-1/2 text-white text-[12px] font-semibold tracking-wider uppercase px-4 py-1 rounded-full shadow-md whitespace-nowrap ${
                        isPopular ? "bg-[#1a73e8]" : "bg-[#e37400]"
                      }`}
                    >
                      {meta.badge}
                    </div>
                  )}

                  <div className="flex flex-col flex-grow">
                    {/* Header */}
                    <div className="mb-6 pb-5 border-b border-[#f1f3f4]">
                      <h3 className="text-[20px] font-medium text-[#202124] mb-1.5 leading-snug">
                        {ele.name}
                      </h3>
                      <p className="text-[#5f6368] text-[13.5px] leading-relaxed min-h-[38px]">
                        {ele.description}
                      </p>
                    </div>

                    {/* Price */}
                    <div className="mb-8">
                      <div className="flex items-baseline gap-1.5">
                        <h4 className="text-[42px] font-semibold text-[#1a73e8] leading-none tracking-tight">
                          ${ele.price}
                        </h4>
                        <span className="text-[#5f6368] text-[14px]">/ one-time</span>
                      </div>
                    </div>

                    {/* Features Checklist */}
                    <div className="space-y-3.5 mb-8 flex-grow">
                      {meta.features.map((item, idx) => (
                        <div key={idx} className="flex items-start gap-3">
                          <div className="flex-shrink-0 w-[18px] h-[18px] rounded-full border-[1.5px] border-[#1a73e8] flex items-center justify-center mt-0.5">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="w-[10px] h-[10px] text-[#1a73e8]"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="3"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <polyline points="20 6 9 17 4 12"></polyline>
                            </svg>
                          </div>
                          <span className="text-[14px] text-[#3c4043] leading-snug">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Action Button pinned to bottom */}
                  <button
                    id={`select-plan-${ele.id}`}
                    type="button"
                    onClick={() => handleSelect(ele.id)}
                    className={`w-full py-3.5 rounded-[4px] font-medium text-[15px] cursor-pointer transition-all duration-300 ${
                      isPopular
                        ? "bg-[#1a73e8] text-white hover:bg-[#1557b0] shadow-[0_4px_14px_rgba(26,115,232,0.35)]"
                        : "bg-white border-2 border-[#1a73e8] text-[#1a73e8] hover:!bg-[#1a73e8] hover:!text-white"
                    }`}
                  >
                    Select Package
                  </button>
                </div>
              );
            })}
        </div>

        {/* Footer Actions */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-6 border-t border-[#dadce0] max-w-6xl mx-auto">
          <button
            type="button"
            onClick={backbtn}
            className="px-6 py-2.5 rounded-[4px] border border-[#dadce0] text-[#1a73e8] font-medium hover:bg-[#f1f3f4] bg-white transition-colors flex items-center gap-2 cursor-pointer text-[14px]"
          >
            <i className="fa-solid fa-arrow-left text-xs"></i> Back
          </button>
          <button
            type="button"
            onClick={handleSkip}
            className="text-[14px] text-[#5f6368] hover:text-[#1a73e8] underline transition-colors cursor-pointer bg-transparent border-none"
          >
            No, thanks — I'll take care of this on my own
          </button>
        </div>

      </div>
    </div>
  );
}
